/**
 * WHERE WEB ERROR REPORTS MAY GO, PER SURFACE AND ENVIRONMENT.
 *
 * Every value here is PUBLIC by design: a Sentry DSN is an ingest address with
 * a public key, shipped to every browser, exactly like the Firebase web
 * config. It is committed here rather than in an env file so that changing
 * where a surface reports is a reviewed source change, and so the build, the
 * unit suite and the contract validator all read one table.
 *
 * PER ENVIRONMENT, FIXED IN THE BUILD (Founder decision 2026-10-06, aligned
 * with Mobile, after test traffic burned 81% of the org's monthly error quota
 * in a day; QA made INERT on 2026-10-13, Founder's environment model, Web
 * Director's design B):
 *
 *   dev      OFF     ""           no SDK in the build at all
 *   qa       INERT   surface DSN  Prod's reporter and chunk graph, never sends
 *   staging  ON      surface DSN
 *   prod     ON      surface DSN
 *
 * No sampling anywhere: an environment reports every (budgeted) event, ships
 * a reporter that never sends, or ships none. An EMPTY DSN is the off switch:
 * viteDefines.ts turns it into `__LURA_ERROR_REPORTING_DSN__ === ""`,
 * main.tsx's `if (__LURA_ERROR_REPORTING_DSN__)` becomes a constant `if ("")`,
 * and the bundler drops the SDK from that build entirely (App: no
 * sentryClient chunk; Book: no SDK in its entry). `reportingDsn` refuses ""
 * as well, so even a hand-built call cannot start a reporter in dev.
 *
 * INERT (qa). QA tests Prod's code: the DSN is compiled in, so the entry, the
 * lazily loaded chunk, the SDK's start in idle time, the global handlers,
 * scrubbing and the chunk-blocked path all run as on Prod. Whether an
 * environment SENDS is `REPORTING_SENDS`, a runtime lookup on the env
 * constant, not a build define: a define would let the bundler drop the
 * branch in one build and keep it in the other, and QA would stop testing
 * Prod's code. Two independent layers read it (WEB-SENTRY-03):
 *   1. installErrorReporting (index.ts) gives the client an inert transport;
 *   2. the client's last fence (sentryClient.ts) refuses to hand any envelope
 *      to the real transport.
 * Either alone sends nothing; the seal_sentinel `qa-inert` phase proves each
 * layer alone, and that both removed do send.
 *
 * ONE PROJECT PER SURFACE (lura-book-web, lura-app-web). Staging and Prod
 * share their surface's DSN; the environment is carried by the SDK's
 * `environment` option and the `env` tag, not by the DSN.
 *
 * PRODUCTION REPORTS DIRECTLY, NO RELAY (Founder decision for rc.1; IP storage
 * is off server-side). Every line is written out, not derived, and
 * `tests/unit/error-reporting-config.test.js` pins the whole table, so any
 * change to where an environment reports is a visible, reviewed edit to this
 * table and that test. A relay/tunnel is a later switch, see
 * docs/architecture/web-error-reporting.md.
 */

export type ReportingSurface = "app" | "book";
export type ReportingEnv = "dev" | "qa" | "staging" | "prod";

export const REPORTING_ENVS: readonly ReportingEnv[] = ["dev", "qa", "staging", "prod"];

/**
 * The one ingest origin both projects use. firebase.json's connect-src names
 * exactly this origin for app.* and book.*, and the gate refuses a DSN on any
 * other origin, so a DSN edit cannot silently route around the CSP.
 */
export const SENTRY_INGEST_ORIGIN = "https://o4511901039853568.ingest.us.sentry.io";

const BOOK_DSN = "https://00dfd1da4358c33e04cc9f52026e2732@o4511901039853568.ingest.us.sentry.io/4512195174989824";
const APP_DSN = "https://22395a77340c7b005d3835497067a5ce@o4511901039853568.ingest.us.sentry.io/4512195181805568";

export const ERROR_REPORTING_DSN: Readonly<Record<ReportingSurface, Readonly<Record<ReportingEnv, string>>>> = {
  book: { dev: "", qa: BOOK_DSN, staging: BOOK_DSN, prod: BOOK_DSN },
  app: { dev: "", qa: APP_DSN, staging: APP_DSN, prod: APP_DSN },
};

/**
 * Whether an environment's reporter may SEND. Written out, never derived from
 * the DSN table: qa carries a DSN and sends nothing.
 */
export const REPORTING_SENDS: Readonly<Record<ReportingEnv, boolean>> = {
  dev: false,
  qa: false,
  staging: true,
  prod: true,
};

/** True only for an environment whose reports may leave the page. */
export function reportingSends(env: string): boolean {
  return isReportingEnv(env) && REPORTING_SENDS[env] === true;
}

/**
 * The only hostname each build may report from. A local build (127.0.0.1,
 * localhost), a preview channel, a unit test under jsdom, or a build served
 * from the wrong environment's host reports nothing. Pinned against
 * scripts/lib/hosting-envs.js `hostsFor` by the unit suite, so this table
 * cannot drift from the hosts the deploy actually assembles.
 */
export const REPORTING_HOST: Readonly<Record<ReportingSurface, Readonly<Record<ReportingEnv, string>>>> = {
  app: {
    dev: "dev.app.uselura.com",
    qa: "qa.app.uselura.com",
    staging: "staging.app.uselura.com",
    prod: "app.uselura.com",
  },
  book: {
    dev: "dev.book.uselura.com",
    qa: "qa.book.uselura.com",
    staging: "staging.book.uselura.com",
    prod: "book.uselura.com",
  },
};

export function isReportingEnv(value: string): value is ReportingEnv {
  return (REPORTING_ENVS as readonly string[]).includes(value);
}

/** The committed DSN for a build, or "" — never undefined. "" means no SDK. */
export function dsnFor(surface: ReportingSurface, env: string): string {
  if (!isReportingEnv(env)) return "";
  return ERROR_REPORTING_DSN[surface][env];
}

/**
 * May this page report? All of: a deployed environment, a non-empty DSN on
 * the approved ingest origin, and the page served from that environment's own
 * host. Anything else is `null`, and `null` means nothing is loaded.
 */
export function reportingDsn(surface: ReportingSurface, env: string, dsn: string, hostname: string): string | null {
  if (!isReportingEnv(env)) return null;
  if (dsn === "" || dsn !== ERROR_REPORTING_DSN[surface][env]) return null;
  if (hostname !== REPORTING_HOST[surface][env]) return null;
  try {
    if (new URL(dsn).origin !== SENTRY_INGEST_ORIGIN) return null;
  } catch {
    return null;
  }
  return dsn;
}
